import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  useTheme,
  Typography, 
  Button,
  styled,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Grid,
  Fade
} from '@mui/material';
import { getUserReadings, isLoggedIn, getAllReadingsFromDB, getCurrentUser } from '../services/userReadingService';
import { useNavigate } from 'react-router-dom';
import { languages, useLanguage, translateFramework } from '../data/languages';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';


const ReadingListItem = styled(Paper)(({ theme, isactive }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: theme.spacing(2),
  backgroundColor: isactive === 'true' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(255, 255, 255, 0.8)',
  borderLeft: isactive === 'true' ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
  '&:hover': {
    backgroundColor: isactive === 'true' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(255, 255, 255, 0.95)',
    transform: 'translateX(4px)',
    boxShadow: theme.shadows[3],
    borderRadius: theme.spacing(4),
  }
}));

const ReadingDetailCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)', // #提高背景不透明度
  padding: theme.spacing(4),
  boxShadow: theme.shadows[2],
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'auto',
  zIndex: 1, // #确保内容在背景圆圈之上
}));

const CardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  // gap: theme.spacing(5),
  marginBottom: theme.spacing(8),
  // padding: theme.spacing(3),
}));

const TarotCard = styled(Box)(({ theme }) => ({
  maxWidth: '180px',
  height: 'auto',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  transform: 'perspective(500px) rotateY(15deg)',
  margin: theme.spacing(2), // Added margin instead of gap
  '&:hover': {
    transform: 'perspective(500px) rotateY(0deg) translateY(-8px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
  }
}));

const CardImage = styled('img')({
  width: '100%',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: '10px',
  display: 'block',
});

const ReadingTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Recoleta', serif",
  fontSize: '2.2rem',
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(8),
  color: 'rgba(0, 0, 0, 0.8)',
  textAlign: 'center',
}));

const TruncatedQuestion = styled(Typography)(({ theme }) => ({
  fontFamily: "'Recoleta', serif",
  fontSize: '1.2rem',
  lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const ReadingDate = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(1),
}));

const NoReadingsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  padding: theme.spacing(3),
  textAlign: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.95)', // #更白的背景
  position: 'relative', // #为背景圆圈定位
}));

// Updated ReadingText with hover effect
const ReadingText = styled(Typography)(({ theme }) => ({
  fontFamily: "'EB Garamond', serif",
  fontSize: '1.25rem',
  lineHeight: 1.8,
  color: 'rgba(0, 0, 0, 0.8)',
  marginBottom: theme.spacing(5),
  // Remove center alignment
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(4px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-2px)',
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
}));

const ReadingContent = styled(Box)(({ theme }) => ({
  flex: 1,
  maxWidth: '600px',
  display: 'flex',
  flexDirection: 'column',
}));

// Updated InteractiveButton
const InteractiveButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(8),
  borderRadius: theme.spacing(4),
  fontSize: '0.85rem',
  padding: theme.spacing(0.8, 2.5),
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: theme.palette.primary.main,
  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
  textTransform: 'none',
  zIndex: 10,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
    transform: 'translateY(-2px)'
  },
  transition: 'all 0.3s ease'
}));

// Timestamp display at bottom
const TimestampDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: theme.spacing(6, 0, 4),
  color: 'rgba(0, 0, 0, 0.5)',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '0.9rem',
  gap: theme.spacing(1),
}));

const CompactStressLevelComparison = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  margin: theme.spacing(4, 5, 2),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
}));

const CompactStressLevelIcon = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: color,
}));

const StressLevelLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '0.8rem',
  color: 'rgba(0, 0, 0, 0.6)',
  textAlign: 'center',
}));

// Invisible scrollbar container - Enhance the CSS to fully hide scrollbars
const InvisibleScrollContainer = styled(Box)(({ theme }) => ({
  width: '75%', 
  height: '100vh',
  overflow: 'auto', 
  padding: theme.spacing(8),
  backgroundColor: 'rgba(252, 252, 255, 0.95)', // #更接近白色的背景
  position: 'relative',
  '&::-webkit-scrollbar': { width: '0', display: 'none' },
  '-ms-overflow-style': 'none',
  'scrollbarWidth': 'none',
  'overflow-y': 'scroll',
  'scrollbar-width': 'none',
}));

// #装饰性渐变圆圈背景
const BackgroundCircles = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 0,
  '& > div': {
    position: 'absolute',
    borderRadius: '50%',
    opacity: 0.8,
  },
  '& > div:nth-of-type(1)': {
    width: '350px',
    height: '350px',
    top: '5%',
    right: '8%',
    background: 'radial-gradient(circle, rgba(25, 118, 210, 0.15) 0%, rgba(25, 118, 210, 0.08) 40%, rgba(25, 118, 210, 0.03) 70%, transparent 100%)',
  },
  '& > div:nth-of-type(2)': {
    width: '200px',
    height: '200px',
    bottom: '20%',
    left: '5%',
    background: 'radial-gradient(circle, rgba(103, 58, 183, 0.12) 0%, rgba(103, 58, 183, 0.06) 40%, rgba(103, 58, 183, 0.02) 70%, transparent 100%)',
  },
  '& > div:nth-of-type(3)': {
    width: '150px',
    height: '150px',
    top: '55%',
    right: '30%',
    background: 'radial-gradient(circle, rgba(156, 39, 176, 0.1) 0%, rgba(156, 39, 176, 0.05) 40%, rgba(156, 39, 176, 0.02) 70%, transparent 100%)',
  }
});

const FrameworkTooltip = styled(Box)(({ theme }) => ({
  position: 'fixed',
  right: theme.spacing(12),
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  padding: theme.spacing(2, 3),
  maxWidth: '250px',
  zIndex: 100,
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

// Create a custom event for auth state changes
export const AUTH_STATE_CHANGE_EVENT = 'authStateChange';

// Helper function to trigger the auth state change event
export const notifyAuthStateChange = () => {
  const event = new CustomEvent(AUTH_STATE_CHANGE_EVENT);
  window.dispatchEvent(event);
};

const StressLevelComparison = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const StressLevelIcon = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  color: color,
}));

const StressLevelArrow = styled(Typography)(({ theme }) => ({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '1.2rem',
  color: theme.palette.primary.main,
}));

export default function UserReadings() {
  const navigate = useNavigate();
  const [readings, setReadings] = useState([]);
  const [selectedReading, setSelectedReading] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredParagraph, setHoveredParagraph] = useState(null);
  const [mousePosition, setMousePosition] = useState({ y: 0 });
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const paragraphRefs = useRef([]);
  const currentLang = useLanguage(); // 响应式语言获取
  const config = languages[currentLang] || languages['en']; // 获取语言配置，带fallback

  const theme = useTheme();

  // Listen for auth state changes
  useEffect(() => {
    const handleAuthChange = () => {
      console.log("Auth state changed, refreshing readings");
      setCurrentUser(getCurrentUser());
      setIsLoading(true);
      fetchReadings();
    };

    // Add event listeners for auth state changes
    window.addEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthChange);
    
    // Check auth state periodically (as a fallback)
    const authCheckInterval = setInterval(() => {
      const user = getCurrentUser();
      if ((user && !currentUser) || (!user && currentUser)) {
        console.log("Auth state changed (detected by interval)");
        setCurrentUser(user);
        fetchReadings();
      }
    }, 5000);
    
    return () => {
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthChange);
      clearInterval(authCheckInterval);
    };
  }, [currentUser]);

  // Function to fetch readings based on auth state
  const fetchReadings = async () => {
    setIsLoading(true);
    try {
      if (isLoggedIn()) {
        console.log('User is logged in, fetching readings from DB');
        const dbReadings = await getAllReadingsFromDB();
        if (dbReadings.success) {
          // Sort readings by timestamp in descending order
          const sortedReadings = dbReadings.readings.sort((a, b) => 
            new Date(b.timestamp) - new Date(a.timestamp)
          );
          setReadings(sortedReadings);
          if (sortedReadings.length > 0) {
            setSelectedReading(sortedReadings[0]);
          } else {
            setSelectedReading(null);
          }
        } else {
          console.error('Failed to fetch readings from DB:', dbReadings.message);
          const localReadings = getUserReadings();
          // Sort local readings by timestamp in descending order
          const sortedLocalReadings = localReadings.sort((a, b) => 
            new Date(b.timestamp) - new Date(a.timestamp)
          );
          setReadings(sortedLocalReadings);
          if (sortedLocalReadings.length > 0) {
            setSelectedReading(sortedLocalReadings[0]);
          } else {
            setSelectedReading(null);
          }
        }
      } else {
        console.log('User is not logged in, fetching from local storage');
        const localReadings = getUserReadings();
        // Sort local readings by timestamp in descending order
        const sortedLocalReadings = localReadings.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        setReadings(sortedLocalReadings);
        if (sortedLocalReadings.length > 0) {
          setSelectedReading(sortedLocalReadings[0]);
        } else {
          setSelectedReading(null);
        }
      }
    } catch (error) {
      console.error('Error fetching readings:', error);
      const localReadings = getUserReadings();
      // Sort local readings by timestamp in descending order
      const sortedLocalReadings = localReadings.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setReadings(sortedLocalReadings);
      if (sortedLocalReadings.length > 0) {
        setSelectedReading(sortedLocalReadings[0]);
      } else {
        setSelectedReading(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Reset paragraph refs when selected reading changes
  useEffect(() => {
    if (selectedReading?.answer) {
      const paragraphCount = selectedReading.answer.split('\n\n').length;
      paragraphRefs.current = Array(paragraphCount).fill().map(() => React.createRef());
    }
  }, [selectedReading]);

  // Initial fetch of readings
  useEffect(() => {
    fetchReadings();
  }, []);

  const handleReadingClick = (reading) => {
    setSelectedReading(reading);
  };

  const handleViewFullReading = () => {
    navigate('/detail/', { 
      state: { 
        readingData: {
          ...selectedReading,
          cards: selectedReading.cards,
          question: selectedReading.question,
          framework: selectedReading.framework,
          answer: selectedReading.answer,
          history: selectedReading.history || [],
          initialStressLevel: selectedReading.initialStressLevel,
          finalStressLevel: selectedReading.finalStressLevel,
        },
        fromHistory: true,
        character: selectedReading.character
      }
    });
  };

  // Function to get framework label based on paragraph index
  const getFrameworkLabel = (index) => {
    // if (!selectedReading?.framework) return 'Reading section';
    // console.log("framework from storage", selectedReading?.framework, typeof selectedReading?.framework);
    
    // Split the framework title by hyphen to get the individual parts
    const translatedFramework = translateFramework(selectedReading?.framework, currentLang);
    const frameworkParts = translatedFramework?.split('-');
    const paragraphs = selectedReading.answer?.split('\n\n') || [];

    console.log("frameworkParts", frameworkParts);
    
    // If there are framework parts and we have the corresponding paragraph
    if (frameworkParts.length > 0 && index < frameworkParts.length) {
      return frameworkParts[index];
    } else if (index >= frameworkParts.length && index < paragraphs.length - 1) {
      // For additional paragraphs beyond the framework parts
      return config.userReadings.additionalInsights;
    } else if (index === paragraphs.length - 1) {
      // Last paragraph is typically the summary
      return config.userReadings.summary;
    }
    
    return config.userReadings.readingSection;
  };

  // Function to get the middle position of a paragraph
  const getParagraphMiddlePosition = (index) => {
    const ref = paragraphRefs.current[index];
    if (ref && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      return rect.top + (rect.height / 2);
    }
    return mousePosition.y; // Fallback to cursor position
  };

  // Format the reading answer into paragraphs with hover functionality and refs
  const renderReadingParagraphs = (answer) => {
    if (!answer) return null;

    return answer.split('\n\n').map((paragraph, index) => (
      <ReadingText 
        key={index}
        ref={paragraphRefs.current[index]}
        onMouseEnter={() => {
          setHoveredParagraph(index);
          // Position will be calculated from the ref
        }}
        onMouseLeave={() => setHoveredParagraph(null)}
      >
        {paragraph}
      </ReadingText>
    ));
  };

  // Format timestamp nicely
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Add this function before the return statement
  const getSentimentIcon = (level) => {
    switch (level) {
      case 1:
        return { Icon: SentimentVeryDissatisfiedIcon, color: '#d32f2f', label: config.userReadings.sentimentLabels[1] };
      case 2:
        return { Icon: SentimentDissatisfiedIcon, color: '#ef6c00', label: config.userReadings.sentimentLabels[2] };
      case 3:
        return { Icon: SentimentNeutralIcon, color: '#ffb300', label: config.userReadings.sentimentLabels[3] };
      case 4:
        return { Icon: SentimentSatisfiedAltIcon, color: '#689f38', label: config.userReadings.sentimentLabels[4] };
      case 5:
        return { Icon: SentimentVerySatisfiedIcon, color: '#2e7d32', label: config.userReadings.sentimentLabels[5] };
      default:
        return { Icon: SentimentNeutralIcon, color: '#ffb300', label: config.userReadings.notRated };
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!readings || readings.length === 0) {
    return (
      <NoReadingsBox>
        <BackgroundCircles> {/* #无阅读状态的背景装饰 */}
          <div />
          <div />
          <div />
        </BackgroundCircles>
        <Typography variant="h4" sx={{ mb: 2, fontFamily: "'Recoleta', serif", zIndex: 1, position: 'relative' }}>
          {config.userReadings.noReadingsTitle}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, zIndex: 1, position: 'relative' }}>
          {config.userReadings.noReadingsText}
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/character')}
          sx={{ zIndex: 1, position: 'relative' }}
        >
          {config.userReadings.startNewReading}
        </Button>
      </NoReadingsBox>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh', 
      overflow: 'hidden',
      backgroundColor: 'rgba(255, 255, 255, 0.9)', // #更白的背景色
      overflowY: 'hidden',
      overflowX: 'hidden',
      maxHeight: '100vh',
      position: 'fixed',
      top: 36,
      left: 0,
      right: 0,
      bottom: 0,
      padding: 0,
      margin: 0
    }}>
      <Box sx={{ 
        width: '25%', 
        height: '100vh',
        overflow: 'auto',
        p: 6,
        borderRight: '1px solid rgba(0,0,0,0.08)',
        '&::-webkit-scrollbar': { width: '0', display: 'none' },
        '-ms-overflow-style': 'none',
        'scrollbarWidth': 'none',
        'overflow-y': 'scroll',
        'scrollbar-width': 'none',
      }}>
        <Typography variant="h5" sx={{ mb: 4, fontFamily: "'Recoleta', serif", pl: 1 }}>
          {config.userReadings.yourReadings}
        </Typography>
        
        <Box>
          {readings.map((reading) => (
            <ReadingListItem 
              key={reading.id}
              isactive={selectedReading?.id === reading.id ? 'true' : 'false'}
              onClick={() => handleReadingClick(reading)}
            >
              <TruncatedQuestion>
                {reading.question}
              </TruncatedQuestion>
              <ReadingDate>
                {new Date(reading.timestamp).toLocaleDateString()} • {reading.cards?.split(', ').length || 0} cards
              </ReadingDate>
            </ReadingListItem>
          ))}
        </Box>
      </Box>
      
      <InvisibleScrollContainer>
        <BackgroundCircles> {/* #添加背景装饰圆圈 */}
          <div />
          <div />
          <div />
        </BackgroundCircles>
        {selectedReading ? (
          <ReadingDetailCard>
            <ReadingTitle>{selectedReading.question}</ReadingTitle>
            
            {/* Stress level comparison below title */}
            {selectedReading.initialStressLevel && (
              <CompactStressLevelComparison sx={{ 
                margin: theme.spacing(0, 'auto', 4),
                maxWidth: '400px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
              }}>
                <CompactStressLevelIcon color={getSentimentIcon(selectedReading.initialStressLevel).color}>
                  {(() => {
                    const { Icon } = getSentimentIcon(selectedReading.initialStressLevel);
                    return Icon && <Icon sx={{ fontSize: 32 }} />;
                  })()}
                  <StressLevelLabel>{config.userReadings.beforeReading}</StressLevelLabel>
                </CompactStressLevelIcon>
                
                <Typography sx={{ 
                  fontSize: '1.4rem', 
                  color: theme.palette.primary.main,
                  margin: theme.spacing(0, 2),
                  fontWeight: 500
                }}>
                  →
                </Typography>
                
                <CompactStressLevelIcon color={getSentimentIcon(selectedReading.finalStressLevel).color}>
                  {(() => {
                    const { Icon } = getSentimentIcon(selectedReading.finalStressLevel);
                    return Icon && <Icon sx={{ fontSize: 32 }} />;
                  })()}
                  <StressLevelLabel>{config.userReadings.afterReading}</StressLevelLabel>
                </CompactStressLevelIcon>
              </CompactStressLevelComparison>
            )}
            
            <CardContainer>
              {selectedReading.cards?.split(', ').map((card, index) => (
                <TarotCard key={index}>
                  <CardImage src={`/cards/${card}`} alt={card} />
                </TarotCard>
              )) || []}
            </CardContainer>
            
            <ContentContainer>
              <ReadingContent>
                {renderReadingParagraphs(selectedReading.answer)}
                
                <TimestampDisplay>
                  <AccessTimeIcon fontSize="small" />
                  {formatTimestamp(selectedReading.timestamp)}
                </TimestampDisplay>
              </ReadingContent>
            </ContentContainer>
            
            {/* Framework tooltip that appears on hover */}
            <Fade in={hoveredParagraph !== null}>
              <FrameworkTooltip 
                sx={{ 
                  top: hoveredParagraph !== null 
                    ? getParagraphMiddlePosition(hoveredParagraph) - 30
                    : mousePosition.y 
                }}
              >
                <Typography sx={{ 
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}>
                  {hoveredParagraph !== null && getFrameworkLabel(hoveredParagraph)}
                </Typography>
              </FrameworkTooltip>
            </Fade>
            
            <InteractiveButton
              onClick={handleViewFullReading}
              endIcon={<OpenInNewIcon fontSize="small" />}
            >
              {config.userReadings.interactiveReading}
            </InteractiveButton>
          </ReadingDetailCard>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: 'rgba(0,0,0,0.4)'
          }}>
            <Typography variant="h5" fontFamily="'Recoleta', serif">
              {config.userReadings.selectReadingToView}
            </Typography>
          </Box>
        )}
      </InvisibleScrollContainer>
    </Box>
  );
} 